import Image from 'app/Components/Shared/Image/image';
import React, {useState, useEffect, useCallback, useRef, useContext, memo, useMemo} from 'react';
import {ComponentProps} from '@episerver/spa-core/EpiComponent';
import SwiperCore, {Autoplay} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import HomepageContentsCarrouselBlockData from 'Models/Content/HomepageContentsCarrouselBlockData';
import {isMobile} from 'react-device-detect';
import HiddenStopAndStartButton from 'app/utilities/HiddenStopAndStartButton';
import classNames from 'classnames';
import 'swiper/swiper.scss';
import ContentCarouselItemBlockData from 'Models/Content/ContentCarouselItemBlockData';
import {useInView} from 'react-intersection-observer';
import {getImageProps, ImageProps} from 'app/utilities/ImageProps';
import {HomepageCellcomTvContext} from '../HomepageCellcomTvBlock/HomepageCellcomTvContext';

SwiperCore.use([Autoplay]);

const TRANSITION_TIME = 500;

type sliders = {
  name: string;
  image: ImageProps;
};

const HomepageContentsCarrouselBlock = ({data, context}: ComponentProps<HomepageContentsCarrouselBlockData>) => {
  const sliders: sliders[] = useMemo(() => {
    return data.items.expandedValue.map((item: ContentCarouselItemBlockData) => ({
      name: item.title?.value,
      image: getImageProps(item.image, context),
    }));
  }, [data.items]);

  const [swiperInstence, setSwiperInstence] = useState<SwiperCore>(null);
  const [swiperIsStop, setSwiperIsStop] = useState<Boolean>(false);
  const [swiperAutoPlay, setSwiperAutoPlay] = useState('Stop');
  const [isSmall, setIsSmall] = useState(false);
  const [imageScale, setImageScale] = useState(0);
  const {ref: bottomRef, inView: bottmInView} = useInView({triggerOnce: true, initialInView: false});
  const {setVisible, visible} = useContext(HomepageCellcomTvContext);
  const [parentTop, setParentTop] = useState(0);

  const setSizes = () => {
    const scaleX = window.innerWidth / (isMobile ? 133 : 194);
    const scaleY = window.innerHeight / (isMobile ? 155 : 194);

    setImageScale(Math.max(scaleX, scaleY));
  };

  useEffect(() => {
    setSizes();
  }, []);

  useEffect(() => {
    if (bottmInView) {
      setVisible(true);
    }
  }, [bottmInView, isMobile]);

  useEffect(() => {
    if (visible) {
      setIsSmall(true);
    }
  }, [visible]);

  useEffect(() => {
    if (!swiperInstence) return;

    swiperInstence?.autoplay?.stop();

    if (!isSmall) {
      swiperInstence?.autoplay?.stop();
    } else {
      setTimeout(() => {
        if (!swiperIsStop) {
          swiperInstence?.autoplay?.start();
        }
      }, TRANSITION_TIME);
    }
  }, [swiperInstence, isSmall]);

  useEffect(() => {
    if (!swiperInstence) return;

    if (swiperIsStop || !isSmall) {
      swiperInstence?.autoplay?.stop();
      setSwiperAutoPlay('Start');
    } else {
      swiperInstence?.autoplay?.start();
      setSwiperAutoPlay('Stop');
    }
  }, [swiperIsStop, swiperInstence]);

  const blockCallback = useCallback((node: HTMLDivElement) => {
    if (node) {
      setParentTop(node.offsetTop);
    }
  }, []);

  return (
    <>
      <div
        ref={blockCallback}
        className={classNames('HomepageContentsCarrouselBlock', {notFull: isSmall})}
        style={{transitionDuration: `${TRANSITION_TIME}ms`, marginTop: `-${parentTop}px`}}>
        <Swiper
          className={classNames('HomepageContentsCarrouselBlock__swiper', {notFull: isSmall})}
          style={{transform: `scale(${imageScale}) translate3d(0, 0, 0)`}}
          onSwiper={(swiper) => {
            setSwiperInstence(swiper);
          }}
          autoplay={{delay: 0, disableOnInteraction: false}}
          slidesPerView='auto'
          speed={4000}
          centeredSlides
          loop
          loopedSlides={sliders.length || 1}>
          {sliders.map((item, i) => (
            <SwiperSlide key={i} className={`HomepageContentsCarrouselBlock__slide`}>
              <Image {...item.image} alt='תמונות של סדרות רצות' className='HomepageContentsCarrouselBlock__image' />
            </SwiperSlide>
          ))}
          <HiddenStopAndStartButton
            className=''
            onClick={() => setSwiperIsStop((prevState) => !prevState)}
            swiperAutoPlay={swiperAutoPlay}
          />
        </Swiper>
      </div>
      <div ref={bottomRef} className={classNames('HomepageContentsCarrouselBlock__anchor bottom-anchor', {notFull: isSmall})}></div>
    </>
  );
};

export default memo(HomepageContentsCarrouselBlock);
